// Which verbs this desk offers, by the rule the server refuses them by.
//
// All six come from the desk: the equipment profile and the branch's overrides
// decide what a service point withholds, and the server sends the result on the
// desk's view. This only decides which buttons to draw. Unknown is everything
// allowed, as it is on the server: a desk that has not loaded must not lose its buttons.
import type { StaffKey } from './staffText'
import type { CounterView, ServicePointVerb } from './types'
import { SEND_TARGETS, type SendTarget } from './transfer'

export interface Offers {
  transfer: boolean
  send: boolean
  park: boolean
  recall: boolean
  walkDirect: boolean
  quickServe: boolean
}

export function deskOffers(desk: Pick<CounterView, 'withheld'> | undefined, canIssue = true): Offers {
  const off = new Set<ServicePointVerb>(desk?.withheld ?? [])
  return {
    transfer: !off.has('transfer'),
    send: !off.has('send'),
    park: !off.has('park'),
    recall: !off.has('recall'),
    // Both issue a ticket, which the server refuses to a role without ticket.issue.
    walkDirect: canIssue && !off.has('walk_direct'),
    quickServe: canIssue && !off.has('quick_serve'),
  }
}

/** The Transfer panel's destinations this desk may use: a service is a transfer,
 *  a desk or a colleague is a send. */
export function offeredTargets(offers: Offers): SendTarget[] {
  return SEND_TARGETS.filter((t) => (t === 'service' ? offers.transfer : offers.send))
}

/** A switched-off list with one verb turned on or off, as a sorted set. Empty,
 *  never undefined, when everything is allowed. */
export function withVerb<V extends string>(withheld: readonly V[] | undefined, verb: V, offered: boolean): V[] {
  const rest = (withheld ?? []).filter((v) => v !== verb)
  return (offered ? rest : [...rest, verb]).sort()
}

/** A switched-off list as the draft holds it: nothing at all rather than an empty
 *  list when everything is allowed, so a box ticked off and on again reads as unchanged. */
export function withheldForDraft<V extends string>(withheld: readonly V[]): V[] | undefined {
  return withheld.length === 0 ? undefined : [...withheld]
}

export const DESK_SWITCHES: readonly { verb: ServicePointVerb; label: StaffKey; hint: StaffKey }[] = [
  { verb: 'transfer', label: 'catalogue.switch.transfer', hint: 'catalogue.switch.transferHint' },
  { verb: 'send', label: 'catalogue.switch.send', hint: 'catalogue.switch.sendHint' },
  { verb: 'park', label: 'catalogue.switch.park', hint: 'catalogue.switch.parkHint' },
  { verb: 'recall', label: 'catalogue.switch.recall', hint: 'catalogue.switch.recallHint' },
  { verb: 'walk_direct', label: 'catalogue.switch.walk_direct', hint: 'catalogue.switch.walk_directHint' },
  { verb: 'quick_serve', label: 'catalogue.switch.quick_serve', hint: 'catalogue.switch.quick_serveHint' },
]
